"use client";

import toastStore from "@/src/store/toastStore";
import {
  Toaster,
  ToastIntent,
  ToastPosition,
  useId,
  useToastController,
} from "@fluentui/react-components";
import React, { createContext, JSX, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

interface ToastOptions {
  position?: ToastPosition;
  intent?: ToastIntent;
  timeout?: number;
}

interface ToastContextType {
  showToast: (
    content: JSX.Element,
    options: ToastOptions,
    throttled?: boolean,
  ) => string | undefined;
  closeToast: (id: string) => void;
}

export const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const id = useId("app-toaster");
  const { dispatchToast, dismissToast } = useToastController(id);

  const showToast = useCallback(
    (content: JSX.Element, options: ToastOptions, throttled?: boolean) => {
      // console.log("[DEBUG] ShowToast");
      const toastId = uuidv4();
      const key = content?.props?.title ?? JSON.stringify(content);
      const { has, add, remove } = toastStore.getState();
      if (has(key)) {
        if (throttled) {
          if (process.env.NODE_ENV === "development") {
            console.debug(`[ToastProvider] Suppressed duplicate toast: "${key}"`);
          }
          return undefined;
        }
        remove(key);
      }
      add(key);
      dispatchToast(content, {
        toastId,
        intent: options.intent || "info",
        position: options.position || "bottom-end",
        timeout: options.timeout,
        onStatusChange: (event, data) => {
          if (data.status === "dismissed") {
            remove(key);
          }
        },
      });
      return toastId;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatchToast],
  );

  const closeToast = useCallback(
    (id: string) => {
      dismissToast(id);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [dismissToast],
  );

  return (
    <ToastContext.Provider value={{ showToast, closeToast }}>
      {children}
      <Toaster toasterId={id} />
    </ToastContext.Provider>
  );
};
